// Store timing utility functions
// Based on the timing logic from ConfirmOrderPage.js and LocationCard.jsx

import {
  convertStoreTimeToUserLocal,
  getTimezoneDisplayInfo,
} from '../Pages/CheckoutPage/utils';

/**
 * Ensures a time string is always in 12-hour AM/PM format
 * @param {string} timeStr - Time string in any format
 * @returns {string} - Time string in 12-hour AM/PM format
 */
export const ensureAmPmFormat = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return timeStr;

  // If already contains AM/PM, return as-is (case-insensitive)
  if (/(AM|PM)/i.test(timeStr)) return timeStr;

  // Try to parse HH:MM or HH:MM:SS (24-hour) and format to 12-hour
  const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return timeStr;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10) || 0;

  const d = new Date();
  d.setHours(hours, minutes, 0, 0);

  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      hourCycle: 'h12',
    }).format(d);
  } catch (e) {
    console.error('Error formatting time to AM/PM:', e);
    return timeStr;
  }
};

/**
 * Gets the current store status (open/closed) and next opening time
 * @param {Object} store - Store object with timing array and timezone
 * @returns {Object} - { isOpen, message, nextOpenTime, nextOpenDay }
 */
export const getStoreStatus = (store) => {
  if (!Array.isArray(store?.timing) || store.timing.length === 0) {
    return {
      isOpen: false,
      message: 'Closed',
      nextOpenTime: null,
      nextOpenDay: null,
    };
  }

  // Get current weekday and time in store timezone (24h HH:MM)
  const tz = store.timezone || 'UTC';
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  });

  const parts = formatter.formatToParts(new Date());
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
  const minute = parseInt(
    parts.find((p) => p.type === 'minute')?.value || '0',
    10
  );
  const weekdayShort = (
    parts.find((p) => p.type === 'weekday')?.value || ''
  ).slice(0, 3);
  const minutesNow = hour * 60 + minute;

  // Map short day names to store timing format
  const mapShortToNew = {
    Thu: 'THU',
    Fri: 'FRI',
    Sat: 'SAT',
    Sun: 'SUN',
    Mon: 'MON',
    Tue: 'TUE',
    Wed: 'WED',
  };

  const dayOrder = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const todayKey = mapShortToNew[weekdayShort];
  const todayIndex = dayOrder.indexOf(todayKey);

  // Find all timing entries for today
  const todayTimings = store.timing.filter((t) => t.dayOfWeek === todayKey);

  // Parse time strings to minutes
  const parseMinutes = (hhmmss) => {
    const [h, m] = hhmmss.split(':').map((s) => parseInt(s || '0', 10));
    return (h || 0) * 60 + (m || 0);
  };

  // Separate overflow entry (starts at 00:00) from main entry
  const overflowEntry = todayTimings.find(
    (t) => t.startLocalTime && parseMinutes(t.startLocalTime) === 0
  );
  const mainEntry = todayTimings.find(
    (t) => t.startLocalTime && parseMinutes(t.startLocalTime) > 0
  );

  // First check if we're in the overflow period from yesterday
  if (
    overflowEntry &&
    overflowEntry.startLocalTime &&
    overflowEntry.endLocalTime
  ) {
    const overflowEndMin = parseMinutes(overflowEntry.endLocalTime);
    if (minutesNow < overflowEndMin) {
      // We're in the overflow period from yesterday, store is open
      return {
        isOpen: true,
        message: 'Open now',
        nextOpenTime: null,
        nextOpenDay: null,
      };
    }
  }

  // Now check the main entry for today
  const today = mainEntry || todayTimings[0];

  if (!today || !today.startLocalTime || !today.endLocalTime) {
    return {
      isOpen: false,
      message: 'Closed today',
      nextOpenTime: null,
      nextOpenDay: null,
    };
  }

  const startMin = parseMinutes(today.startLocalTime);
  const endMin = parseMinutes(today.endLocalTime);

  // Check if currently open
  const isOpen = minutesNow >= startMin && minutesNow <= endMin;
  if (isOpen) {
    return {
      isOpen: true,
      message: 'Open now',
      nextOpenTime: null,
      nextOpenDay: null,
    };
  }

  // Helper to convert 24h time to 12h format with timezone conversion
  const to12Hour = (hhmmss) => {
    const storeTimezone = store.timezone || 'UTC';
    const convertedTime = convertStoreTimeToUserLocal(hhmmss, storeTimezone);
    return ensureAmPmFormat(convertedTime);
  };

  // If we're before opening time today, show today's opening time
  if (minutesNow < startMin) {
    return {
      isOpen: false,
      message: `Opens at ${to12Hour(today.startLocalTime)}`,
      nextOpenTime: to12Hour(today.startLocalTime),
      nextOpenDay: 'today',
    };
  }

  // If we're after closing time today, find next opening time
  // Check next 7 days for opening time
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (todayIndex + i) % 7;
    const nextDayKey = dayOrder[nextDayIndex];

    // Get all timing entries for next day and filter for main entry (not overflow)
    const nextDayTimings = store.timing.filter(
      (t) => t.dayOfWeek === nextDayKey
    );
    const nextDayMain = nextDayTimings.find(
      (t) => t.startLocalTime && parseMinutes(t.startLocalTime) > 0
    );
    const nextDay = nextDayMain || nextDayTimings[0];

    if (nextDay && nextDay.startLocalTime) {
      const dayNames = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      const nextDayName = dayNames[nextDayIndex];
      const openTime = to12Hour(nextDay.startLocalTime);

      return {
        isOpen: false,
        message: `Opens ${i === 1 ? 'tomorrow' : nextDayName} at ${openTime}`,
        nextOpenTime: openTime,
        nextOpenDay: i === 1 ? 'tomorrow' : nextDayName,
      };
    }
  }
  return {
    isOpen: false,
    message: 'Closed',
    nextOpenTime: null,
    nextOpenDay: null,
  };
};

/**
 * Gets today's closing time for display
 * @param {Object} store - Store object with timing array and timezone
 * @returns {string} - Formatted closing time or default message
 */
export const getTodaysClosingTime = (store) => {
  if (!Array.isArray(store?.timing) || store.timing.length === 0) {
    return '9:00 PM';
  }

  const tz = store.timezone || 'UTC';
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  });

  const parts = formatter.formatToParts(new Date());
  const weekdayShort = (
    parts.find((p) => p.type === 'weekday')?.value || ''
  ).slice(0, 3);

  const mapShortToNew = {
    Thu: 'THU',
    Fri: 'FRI',
    Sat: 'SAT',
    Sun: 'SUN',
    Mon: 'MON',
    Tue: 'TUE',
    Wed: 'WED',
  };

  const todayKey = mapShortToNew[weekdayShort];

  // Find all timing entries for today
  const todayTimings = store.timing.filter((t) => t.dayOfWeek === todayKey);

  if (!todayTimings || todayTimings.length === 0) {
    return '9:00 PM';
  }

  // Parse time strings to minutes
  const parseMinutes = (hhmmss) => {
    const [h, m] = hhmmss.split(':').map((s) => parseInt(s || '0', 10));
    return (h || 0) * 60 + (m || 0);
  };

  // Separate overflow entry (starts at 00:00) from main entry
  const mainEntry = todayTimings.find(
    (t) => t.startLocalTime && parseMinutes(t.startLocalTime) > 0
  );

  // Prefer main entry, fallback to first entry
  const today = mainEntry || todayTimings[0];

  if (!today || !today.endLocalTime) {
    return '9:00 PM';
  }

  // Check if today extends into tomorrow (ends at 24:00)
  // If so, look for tomorrow's overflow entry to get the actual closing time
  const dayOrder = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const todayIndex = dayOrder.indexOf(todayKey);
  const tomorrowKey = dayOrder[(todayIndex + 1) % 7];
  const tomorrowTimings = store.timing.filter(
    (t) => t.dayOfWeek === tomorrowKey
  );
  const tomorrowOverflow = tomorrowTimings.find(
    (t) => t.startLocalTime && parseMinutes(t.startLocalTime) === 0
  );

  // Use timezone conversion to show closing time in user's local time
  const storeTimezone = store.timezone || 'UTC';

  let closingTime;
  if (
    parseMinutes(today.endLocalTime) === 1440 && // 24:00 = 1440 minutes
    tomorrowOverflow &&
    tomorrowOverflow.endLocalTime
  ) {
    // Today extends into tomorrow, use overflow's end time
    closingTime = convertStoreTimeToUserLocal(
      tomorrowOverflow.endLocalTime,
      storeTimezone
    );
  } else {
    closingTime = convertStoreTimeToUserLocal(
      today.endLocalTime,
      storeTimezone
    );
  }

  // Ensure the time is in AM/PM format
  return ensureAmPmFormat(closingTime);
};

/**
 * Gets timezone display information for the store
 * @param {Object} store - Store object with timezone
 * @returns {Object} - Timezone information for display
 */
export const getStoreTimezoneInfo = (store) => {
  const storeTimezone = store.timezone || 'UTC';
  return getTimezoneDisplayInfo(storeTimezone);
};

/**
 * Format a timing entry for a given day into a user-local 12h range string
 * @param {Object} store - Store object with timing array and timezone
 * @param {string} dayShort - One of Thu, Fri, Sat, Sun, Mon, Tue, Wed
 * @param {boolean} useDelivery - If true, prefer deliveryTiming array on store (if present)
 * @returns {string} - e.g. "11:00 AM - 10:00 PM" or "Closed"
 */
export const formatTimingForDay = (store, dayShort, useDelivery = false) => {
  if (!store) return 'Closed';

  const mapShortToNew = {
    Thu: 'THU',
    Fri: 'FRI',
    Sat: 'SAT',
    Sun: 'SUN',
    Mon: 'MON',
    Tue: 'TUE',
    Wed: 'WED',
  };

  const dayKey = mapShortToNew[dayShort];
  const timingArray =
    useDelivery && Array.isArray(store?.deliveryTiming)
      ? store.deliveryTiming
      : Array.isArray(store?.timing)
      ? store.timing
      : [];

  // Find all timing entries for this day
  const dayTimings = timingArray.filter((t) => t.dayOfWeek === dayKey);

  if (!dayTimings || dayTimings.length === 0) return 'Closed';

  // Helper to parse time to minutes for comparison
  const parseMinutes = (hhmmss) => {
    const [h, m] = hhmmss.split(':').map((s) => parseInt(s || '0', 10));
    return (h || 0) * 60 + (m || 0);
  };

  // Separate overflow entries (starts at 00:00) from main entries
  const overflowEntry = dayTimings.find(
    (t) => t.startLocalTime && parseMinutes(t.startLocalTime) === 0
  );
  const mainEntry = dayTimings.find(
    (t) => t.startLocalTime && parseMinutes(t.startLocalTime) > 0
  );

  // Get the next day to check for overflow hours
  const dayOrder = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const currentDayIndex = dayOrder.indexOf(dayKey);
  const nextDayKey = dayOrder[(currentDayIndex + 1) % 7];
  const nextDayTimings = timingArray.filter((t) => t.dayOfWeek === nextDayKey);
  const nextDayOverflow = nextDayTimings.find(
    (t) => t.startLocalTime && parseMinutes(t.startLocalTime) === 0
  );

  // If we only have one entry, check if next day has overflow
  if (dayTimings.length === 1) {
    const dayTiming = dayTimings[0];
    if (!dayTiming.startLocalTime || !dayTiming.endLocalTime) return 'Closed';

    const storeTimezone = store.timezone || 'UTC';
    const start = convertStoreTimeToUserLocal(
      dayTiming.startLocalTime,
      storeTimezone
    );

    // If the current day ends at 24:00 and next day has overflow starting at 00:00,
    // use the overflow's end time as the actual closing time
    let end;
    if (
      parseMinutes(dayTiming.endLocalTime) === 1440 && // 24:00 = 1440 minutes
      nextDayOverflow &&
      nextDayOverflow.endLocalTime
    ) {
      end = convertStoreTimeToUserLocal(
        nextDayOverflow.endLocalTime,
        storeTimezone
      );
    } else {
      end = convertStoreTimeToUserLocal(dayTiming.endLocalTime, storeTimezone);
    }

    return `${ensureAmPmFormat(start)} - ${ensureAmPmFormat(end)}`;
  }

  // If we have both overflow and main entry, prioritize the main entry
  // The overflow represents hours from the previous day
  if (mainEntry && mainEntry.startLocalTime && mainEntry.endLocalTime) {
    const storeTimezone = store.timezone || 'UTC';
    const start = convertStoreTimeToUserLocal(
      mainEntry.startLocalTime,
      storeTimezone
    );

    // Check if this day extends into the next day
    let end;
    if (
      parseMinutes(mainEntry.endLocalTime) === 1440 && // 24:00 = 1440 minutes
      nextDayOverflow &&
      nextDayOverflow.endLocalTime
    ) {
      end = convertStoreTimeToUserLocal(
        nextDayOverflow.endLocalTime,
        storeTimezone
      );
    } else {
      end = convertStoreTimeToUserLocal(mainEntry.endLocalTime, storeTimezone);
    }

    return `${ensureAmPmFormat(start)} - ${ensureAmPmFormat(end)}`;
  }

  // If we only have overflow entry (shouldn't happen in practice, but handle it)
  if (
    overflowEntry &&
    overflowEntry.startLocalTime &&
    overflowEntry.endLocalTime
  ) {
    const storeTimezone = store.timezone || 'UTC';
    const start = convertStoreTimeToUserLocal(
      overflowEntry.startLocalTime,
      storeTimezone
    );
    const end = convertStoreTimeToUserLocal(
      overflowEntry.endLocalTime,
      storeTimezone
    );

    return `${ensureAmPmFormat(start)} - ${ensureAmPmFormat(end)}`;
  }

  return 'Closed';
};
